import React, { useState, useRef } from 'react';
import { IonIcon } from '@ionic/react';
import { close } from 'ionicons/icons';
import './EmailChipInput.css';

/**
 * EmailChipInput - Gmail-style email address chip input
 * Allows entering multiple emails with token/chip display
 * Pressing Enter or comma converts the current text to a chip
 */
const EmailChipInput = ({ 
  label, 
  emails = [], 
  onChange, 
  placeholder = "",
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  // Parse email string to extract email address
  const parseEmail = (emailStr) => {
    const trimmed = emailStr.trim();
    if (!trimmed) return null;
    
    // Match "Name <email@domain.com>" or just "email@domain.com"
    const match = trimmed.match(/<(.+?)>/) || trimmed.match(/([^\s<>]+@[^\s<>]+)/);
    return match ? match[1] : trimmed;
  };

  // Validate email format (basic)
  const isValidEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // Add email chip
  const addEmail = (emailStr) => {
    const email = parseEmail(emailStr);
    if (!email) return;
    
    // Don't add duplicates
    if (emails.some(e => parseEmail(e) === email)) {
      setInputValue('');
      return;
    }
    
    // Add to list
    onChange([...emails, email]);
    setInputValue('');
  };

  // Remove email chip
  const removeEmail = (index) => {
    onChange(emails.filter((_, i) => i !== index));
  };

  // Handle input key events
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      if (inputValue.trim()) {
        addEmail(inputValue);
      }
    } else if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      // Remove last chip on backspace when input is empty
      removeEmail(emails.length - 1);
    }
  };

  // Handle input blur
  const handleBlur = () => {
    setIsFocused(false);
    // Add current input as email if valid
    if (inputValue.trim()) {
      addEmail(inputValue);
    }
  };

  // Handle paste event
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const emailList = pastedText.split(/[,;\s]+/).filter(Boolean);
    
    emailList.forEach(email => {
      const parsed = parseEmail(email);
      if (parsed && !emails.some(e => parseEmail(e) === parsed)) {
        emails.push(parsed);
      }
    });
    
    onChange([...emails]);
    setInputValue('');
  };

  return (
    <div className={`email-chip-input-container ${isFocused ? 'focused' : ''}`}>
      {label && <label className="email-chip-label">{label}</label>}
      <div 
        className="email-chip-input-wrapper"
        onClick={() => inputRef.current?.focus()}
      >
        {/* Email chips */}
        {emails.map((email, index) => {
          const displayEmail = parseEmail(email) || email;
          const isValid = isValidEmail(displayEmail);
          
          return (
            <div 
              key={`${email}-${index}`}
              className={`email-chip ${isValid ? '' : 'invalid'}`}
            >
              <span className="email-chip-text">{displayEmail}</span>
              {!disabled && (
                <button
                  type="button"
                  className="email-chip-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeEmail(index);
                  }}
                  aria-label="Remove email"
                >
                  <IonIcon icon={close} />
                </button>
              )}
            </div>
          );
        })}
        
        {/* Input field */}
        {!disabled && (
          <input
            ref={inputRef}
            type="text"
            className="email-chip-input"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={handleBlur}
            onPaste={handlePaste}
            placeholder={emails.length === 0 ? placeholder : ''}
          />
        )}
      </div>
    </div>
  );
};

export default EmailChipInput;

